import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import PersonaBadge from '../common/PersonaBadge';
import ScoreRing from '../common/ScoreRing';
import { GauntletRun } from '../../types/gymnasium';
import { FiArrowLeft, FiBarChart2, FiCheck, FiX } from 'react-icons/fi';

interface Props {
  run: GauntletRun;
}

const GauntletScorecard: React.FC<Props> = ({ run }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();

  const passRate = run.pass_rate != null ? Math.round(run.pass_rate * 100) : 0;
  const categories = run.category_scores || {};

  const sectionClass = `rounded-lg shadow-lg border-l-4 p-5 ${
    darkMode ? 'bg-gray-800 border-amber-600' : 'bg-white border-amber-500 shadow-sm'
  }`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/gymnasium/gauntlets')} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
          <FiArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-emerald-400">
            Scorecard
          </h1>
          <div className="flex items-center gap-2 mt-0.5">
            <PersonaBadge personaId={run.persona_id} personaName={run.persona_name} size="xs" clickable={false} />
            <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {run.gauntlet_name?.replace(/_/g, ' ')}
            </span>
          </div>
        </div>
      </div>

      {/* Big Score */}
      <div className={`${sectionClass} text-center`}>
        <div className="mb-3">
          <ScoreRing value={run.pass_rate ?? 0} size="lg" />
        </div>
        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {run.total_score} / {run.max_possible} points across {run.scenarios_completed} scenarios
        </p>
      </div>

      {/* Category Breakdown */}
      {Object.keys(categories).length > 0 && (
        <div className={sectionClass}>
          <h2 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <FiBarChart2 className="inline w-4 h-4 mr-1.5 -mt-0.5" />
            Category Breakdown
          </h2>
          <div className="space-y-3">
            {Object.entries(categories).map(([cat, data]) => {
              const pct = data.max > 0 ? Math.round((data.score / data.max) * 100) : 0;
              return (
                <div key={cat}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{cat.replace(/_/g, ' ')}</span>
                    <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{data.score}/{data.max} ({pct}%)</span>
                  </div>
                  <div className={`h-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div
                      className={`h-2 rounded-full transition-all duration-500 ${
                        pct >= 70 ? 'bg-green-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Per-Scenario Summary */}
      <div className={sectionClass}>
        <h2 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Scenarios</h2>
        <div className="space-y-2">
          {run.results?.map((r: any, i: number) => {
            const pct = r.max_score > 0 ? Math.round((r.score / r.max_score) * 100) : 0;
            return (
              <div key={r.scenario_id || i} className={`flex items-center gap-3 p-2 rounded-lg ${darkMode ? 'bg-gray-800/40' : 'bg-gray-50'}`}>
                <span className={`w-5 h-5 flex items-center justify-center rounded-full text-xs ${
                  pct >= 70 ? 'bg-green-600/20 text-green-400' : pct >= 40 ? 'bg-amber-600/20 text-amber-400' : 'bg-red-600/20 text-red-400'
                }`}>
                  {pct >= 70 ? <FiCheck className="w-3 h-3" /> : <FiX className="w-3 h-3" />}
                </span>
                <span className={`text-sm flex-1 truncate ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{r.label}</span>
                <span className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{r.score}/{r.max_score}</span>
                <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{r.category}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-center gap-3">
        <button
          onClick={() => navigate('/gymnasium/radar')}
          className="px-4 py-2 text-sm rounded-lg bg-gradient-to-r from-blue-500 to-indigo-500 text-white hover:from-blue-600 hover:to-indigo-600 transition-all flex items-center gap-2"
        >
          <FiBarChart2 className="w-4 h-4" /> Compare with Others
        </button>
        <button
          onClick={() => navigate('/gymnasium')}
          className={`px-4 py-2 text-sm rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
        >
          Back to Gymnasium
        </button>
      </div>
    </div>
  );
};

export default GauntletScorecard;
